import * as Flac from 'libflacjs/dist/libflac';

export const audioBufferToWav = (audioBuffer: AudioBuffer): Blob => {
	const bytesPerSample = 2;
	const headerSize = 44;
	let size = audioBuffer.length * audioBuffer.numberOfChannels * bytesPerSample + headerSize;
	let buffer = new ArrayBuffer(size);
	let dataView = new DataView(buffer);

	let curPos = 0;

	function appendUint16(value: number) {
		dataView.setUint16(curPos, value, true);
		curPos += 2;
	}

	function appendUint32(value: number) {
		dataView.setUint32(curPos, value, true);
		curPos += 4;
	}

	// Wav Header
	appendUint32(0x46464952); // "RIFF"
	appendUint32(size - 8); // File size minus 8 bytes
	appendUint32(0x45564157); // "WAVE"

	// Data Format Section
	appendUint32(0x20746d66); // "fmt "
	appendUint32(16); // Length of Data Format section
	appendUint16(1); // Format: PCM
	appendUint16(audioBuffer.numberOfChannels); // Number of channels
	appendUint32(audioBuffer.sampleRate); // Sample rate
	appendUint32(audioBuffer.sampleRate * audioBuffer.numberOfChannels * bytesPerSample); // Bytes per second
	appendUint16(audioBuffer.numberOfChannels * bytesPerSample); // Block align
	appendUint16(bytesPerSample * 8); // Bits per sample

	// Sample Data Section
	appendUint32(0x61746164); // "data"
	appendUint32(audioBuffer.length * audioBuffer.numberOfChannels * bytesPerSample); // Length of Sample Data section

	console.assert(curPos === headerSize);

	// Write audio samples to buffer as 16 bit signed ints with interleaved channels
	let channelData = [];
	for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
		channelData.push(audioBuffer.getChannelData(i));
	}

	// Interleave the channels
	for (let frameIdx = 0; frameIdx < audioBuffer.length; frameIdx++) {
		for (let channelIdx = 0; channelIdx < audioBuffer.numberOfChannels; channelIdx++) {
			// Convert sample from a float (-1.0 to 1.0) to a 16 bit signed int (-32768 to 32767)
			let sample = channelData[channelIdx][frameIdx];
			sample = Math.round(sample * 32767);
			sample = Math.max(-32768, Math.min(32767, sample));
			appendUint16(sample);
		}
	}

	let blob = new Blob([buffer], {type: "audio/wav"});
	return blob;
};

export const audioBufferToFlac = (audioBuffer: AudioBuffer): Blob | Error => {
	let flacEncoder = Flac.create_libflac_encoder(
		audioBuffer.sampleRate, // Sample rate
		audioBuffer.numberOfChannels, // Number of channels
		16, // Bits per sample
		5, // Compression
		audioBuffer.length, // Number of samples (optional)
	);

	if (flacEncoder === 0) {
		return new Error("Failed to create Flac Encoder.");
	}

	// The final flac file data will be added in chuncks to this array
	let encodeBuffers: Uint8Array[] = [];
	let writeCallback = function(data: Uint8Array, numberOfBytes: number, samples: number, currentFrame: number) {
		encodeBuffers.push(data);
	}
	
	let status = Flac.init_encoder_stream(flacEncoder, writeCallback);
	if (status !== 0) {
		return new Error("Failed to initialize Flac encoder stream.")
	}

	// Create a Int32Array with 16 bit signed ints and interleaved channels
	let buffer = new Int32Array(audioBuffer.length * audioBuffer.numberOfChannels);
	let dataView = new DataView(buffer.buffer);
	
	let curPos = 0;

	let channelData = [];
	for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
		channelData.push(audioBuffer.getChannelData(i));
	}

	// Interleave the channels
	for (let frameIdx = 0; frameIdx < audioBuffer.length; frameIdx++) {
		for (let channelIdx = 0; channelIdx < audioBuffer.numberOfChannels; channelIdx++) {
			// Convert sample from a float (-1.0 to 1.0) to a 16 bit signed int (-32768 to 32767)
			let sample = channelData[channelIdx][frameIdx];
			sample = Math.round(sample * 32767);
			sample = Math.max(-32768, Math.min(32767, sample));
			
			dataView.setUint32(curPos, sample, true);
			curPos += 4;
		}
	}

	let success = Flac.FLAC__stream_encoder_process_interleaved(flacEncoder, buffer, buffer.length / audioBuffer.numberOfChannels)
	if (!success) {
		return new Error("Error encoding audio buffer to Flac.");
	}

	success = Flac.FLAC__stream_encoder_finish(flacEncoder)
	if (!success) {
		return new Error("Error finishing Flac encoding.");
	}

	Flac.FLAC__stream_encoder_delete(flacEncoder);

	let blob = new Blob(encodeBuffers, {type: "audio/x-flac"});
	return blob;
};
